/**
 * Web-side mirror of `shared/src/util/state-tax.ts`. Web does NOT import
 * from `@hearth/shared` (locked since 5.1). Keep behavior identical to
 * the shared implementation; matching test cases at
 * `web/src/lib/util/state-tax.test.ts` are the contract.
 *
 * See `shared/src/util/state-tax.ts` for IRS / NY tax law citations.
 */

/**
 * Phase 7 Session 1 widening — FilingStatus expanded from 2 to all 5
 * IRS values now that Federal/NY/NYC liability helpers + quarterly tax
 * projection consume it.
 */
export type FilingStatus = "single" | "mfj" | "mfs" | "hoh" | "qss";

// NY 529: only MFJ gets $10K cap; everyone else uses $5K per IT-201-I.
const NY_529_CAP_BY_FILING_STATUS_CENTS: Record<FilingStatus, number> = {
  single: 5_000_00,
  mfj: 10_000_00,
  mfs: 5_000_00,
  hoh: 5_000_00,
  qss: 5_000_00,
};

export interface Ny529DeductionResult {
  deductible_cents: number;
  cap_cents: number;
  excess_cents: number;
}

export function nyState529Deduction(
  contributions_cents: number,
  filing_status: FilingStatus,
): Ny529DeductionResult {
  if (!Number.isInteger(contributions_cents)) {
    throw new Error(
      `nyState529Deduction: contributions_cents must be an integer, got ${contributions_cents}`,
    );
  }
  if (contributions_cents < 0) {
    throw new Error(
      `nyState529Deduction: contributions_cents must be non-negative, got ${contributions_cents}`,
    );
  }
  const cap_cents = NY_529_CAP_BY_FILING_STATUS_CENTS[filing_status];
  const deductible_cents = Math.min(contributions_cents, cap_cents);
  const excess_cents = Math.max(0, contributions_cents - cap_cents);
  return { deductible_cents, cap_cents, excess_cents };
}
